import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MAINT_STATUS } from './maintenanceShared';
import { APPT_STATUS } from '../workshop/AppointmentCard';

const iso = (d) => d.toLocaleDateString('sv-SE');
const mondayOf = (d) => {
    const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
    return m;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const Chip = ({ id, cls, dim, title, line, status, onClick, testId }) => (
    <button onClick={onClick} className={`w-full text-left rounded-sm px-2 py-1.5 text-xs transition-transform hover:-translate-y-0.5 ${cls} ${dim ? 'opacity-50' : ''}`} data-testid={testId}>
        <p className="font-semibold truncate">{title}</p>
        <p className="truncate">{line}</p>
        <p className="truncate opacity-75">{status}</p>
    </button>
);

const DayLoad = ({ info, el }) => {
    if (!info) return null;
    if (info.closed) return <span className="text-[10px] uppercase bg-slate-700 text-white px-1.5 rounded-sm">{el ? 'Κλειστό' : 'Closed'}</span>;
    if (!info.capacity) return null;
    return <span className={`text-[10px] px-1.5 rounded-sm ${info.full ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{info.booked}/{info.capacity}</span>;
};

export const WorkshopWeekCalendar = ({ jobs, appointments = [], availability = {}, el, onSelect, onSelectAppointment, onWeekChange }) => {
    const [start, setStart] = useState(() => mondayOf(new Date()));
    useEffect(() => { if (onWeekChange) onWeekChange(iso(start)); }, [start, onWeekChange]);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    const today = iso(new Date());
    const locale = el ? 'el-GR' : 'en-GB';

    return (
        <div className="fleet-card p-4 space-y-4" data-testid="workshop-week-calendar">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-slate-800" data-testid="cal-week-label">
                    {start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – {days[6].toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setStart(addDays(start, -7))} data-testid="cal-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setStart(mondayOf(new Date()))} data-testid="cal-this-week">{el ? 'Σήμερα' : 'Today'}</Button>
                    <Button size="sm" variant="outline" onClick={() => setStart(addDays(start, 7))} data-testid="cal-next-week"><ChevronRight className="w-4 h-4" /></Button>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
                {days.map((d) => {
                    const key = iso(d);
                    const info = availability[key];
                    const dayJobs = jobs.filter((j) => (j.scheduled_date || '').slice(0, 10) === key);
                    const dayAppts = appointments.filter((a) => a.date === key);
                    const empty = !dayJobs.length && !dayAppts.length;
                    return (
                        <div key={key} className={`min-h-[140px] rounded-sm border p-2 space-y-2 ${key === today ? 'border-[#1B7EB5] bg-[#1B7EB5]/5' : info?.closed ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`} data-testid={`cal-day-${key}`}>
                            <div className="flex items-center justify-between gap-1">
                                <span className="text-xs uppercase text-slate-400">{d.toLocaleDateString(locale, { weekday: 'short' })}</span>
                                <span className="flex items-center gap-1" data-testid={`cal-load-${key}`}><DayLoad info={info} el={el} /><span className={`text-sm font-bold ${key === today ? 'text-[#1B7EB5]' : 'text-slate-700'}`}>{d.getDate()}</span></span>
                            </div>
                            {dayJobs.map((j) => {
                                const s = MAINT_STATUS[j.status] || MAINT_STATUS.scheduled;
                                return <Chip key={j.id} cls={s.cls} dim={j.status === 'completed'} title={j.license_plate} line={j.service_type} status={el ? s.el : s.en} onClick={() => onSelect(j)} testId={`cal-job-${j.id}`} />;
                            })}
                            {dayAppts.map((a) => {
                                const s = APPT_STATUS[a.status] || APPT_STATUS.scheduled;
                                return <Chip key={a.id} cls={`${s.cls} border-l-2 border-indigo-500`} dim={a.status === 'cancelled'} title={`${a.time ? `${a.time} · ` : ''}${a.license_plate}`} line={a.customer_name} status={el ? s.el : s.en} onClick={() => onSelectAppointment(a)} testId={`cal-appt-${a.id}`} />;
                            })}
                            {empty && <p className="text-[11px] text-slate-300">{info?.closed ? '' : (el ? 'Ελεύθερη' : 'Free')}</p>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
